import {
  GridFilterOperator,
  GridRenderCellParams,
  GridFilterItem,
  GridFilterInputValue,
} from "@mui/x-data-grid";

import { Typography } from "@mui/material";
import moment from "moment";

import { isEmpty, isTextEmpty } from "utils/commons";

//icons
import CircleIcon from "@mui/icons-material/Circle";
import BatteryUnknown from "@mui/icons-material/BatteryUnknown";
import Battery0BarIcon from "@mui/icons-material/Battery0Bar";
import Battery1BarIcon from "@mui/icons-material/Battery1Bar";
import Battery2BarIcon from "@mui/icons-material/Battery2Bar";
import Battery3BarIcon from "@mui/icons-material/Battery3Bar";
import Battery4BarIcon from "@mui/icons-material/Battery4Bar";
import Battery5BarIcon from "@mui/icons-material/Battery5Bar";
import Battery6BarIcon from "@mui/icons-material/Battery6Bar";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";

const DATETIME_FORMAT = "YYYY-MM-DD HH:mm:ss";

/**
 * 「Stay」ソート処理
 * @param v1
 * @param v2
 * @param param1
 * @param param2
 * @returns
 */
const staySortComparator = (v1, v2, param1: any, param2: any) => {
  const value1 =
    param1["api"].getRow(param1["id"])["stay"] === "ATTENDED" ? 1 : 0;
  const value2 =
    param2["api"].getRow(param2["id"])["stay"] === "ATTENDED" ? 1 : 0;

  return value1 > value2 ? 1 : value1 < value2 ? -1 : 0;
};

/**
 * 「Stay」描画処理
 * @param params
 * @returns
 */
const stayRenderCell = (params: GridRenderCellParams) => {
  const row = params["row"];
  const stay = row["stay"];

  const style = {
    color: "#FFFFFF",
  };
  if (stay === "ATTENDED") {
    style["color"] = "#a0e8c9";
  } else {
    style["color"] = "#d96296";
  }

  return <CircleIcon fontSize="medium" sx={style} />;
};

/**
 * 「Distance」ソート処理
 * @param v1
 * @param v2
 * @param param1
 * @param param2
 * @returns
 */
const distanceSortComparator = (v1, v2, param1: any, param2: any) => {
  const value1 = param1["api"].getRow(param1["id"])["distance"];
  const value2 = param2["api"].getRow(param2["id"])["distance"];

  return value1.localeCompare(value2);
};

/**
 * 「Distance」描画処理
 * @param params
 * @returns
 */
//Distance表記にメートルを追記
const DISTANCE_METER_LABELS = {
  NONE: "None",
  NEAR: "Near",
  MID: "Mid",
  FAR: "Far",
};

const distanceRenderCell = (params: GridRenderCellParams) => {
  const row = params["row"];
  const distance = row["distance"];
  return <Typography>{DISTANCE_METER_LABELS[distance]}</Typography>;
};

/**
 * 「Battery」フィルター機能定義
 */
const batteryFilterOperators: GridFilterOperator[] = [
  {
    label: "above",
    value: "above",
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (!filterItem.field || !filterItem.value || !filterItem.operator) {
        return null;
      }

      return (params): boolean => {
        const paramsValue = params["row"]["battery"];
        const filterValue = filterItem["value"];
        return Number(paramsValue) >= Number(filterValue);
      };
    },
    InputComponent: GridFilterInputValue,
    InputComponentProps: { type: "number" },
    getValueAsString: (value: number) => `${value}`,
  },
  {
    label: "below",
    value: "below",
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (!filterItem.field || !filterItem.value || !filterItem.operator) {
        return null;
      }

      return (params): boolean => {
        const paramsValue = params["row"]["battery"];
        const filterValue = filterItem["value"];
        return Number(paramsValue) <= Number(filterValue);
      };
    },
    InputComponent: GridFilterInputValue,
    InputComponentProps: { type: "number" },
    getValueAsString: (value: number) => `${value}`,
  },
];

/**
 * 「Battery」ソート処理
 * @param v1
 * @param v2
 * @param param1
 * @param param2
 * @returns
 */
const batterySortComparator = (v1, v2, param1: any, param2: any) => {
  const value1 = param1["api"].getRow(param1["id"])["battery"];
  const value1SortDirection = param2["api"].getSortModel(param1["id"])[0][
    "sort"
  ];
  const value2 = param2["api"].getRow(param2["id"])["battery"];
  const value2SortDirection = param1["api"].getSortModel(param1["id"])[0][
    "sort"
  ];

  if (value1 === -1) {
    if (value1SortDirection === "asc") return 1;
    else return -1;
  } else if (value2 === -1) {
    if (value2SortDirection === "asc") return -1;
    else return 1;
  } else {
    return value1 > value2 ? 1 : value1 < value2 ? -1 : 0;
  }
};

/**
 * 「Battery」描画処理
 * @param params
 * @returns
 */
const batteryRenderCell = (params: GridRenderCellParams) => {
  const row = params["row"];
  const battery = row["battery"];

  let batteryComponent = <BatteryUnknown />;

  if (battery >= 0 && battery < 10)
    batteryComponent = <Battery0BarIcon sx={{ color: "red" }} />;
  else if (battery >= 10 && battery < 20)
    batteryComponent = <Battery1BarIcon sx={{ color: "red" }} />;
  else if (battery >= 20 && battery < 30)
    batteryComponent = <Battery2BarIcon sx={{ color: "orange" }} />;
  else if (battery >= 30 && battery < 40)
    batteryComponent = <Battery3BarIcon sx={{ color: "orange" }} />;
  else if (battery >= 40 && battery < 50)
    batteryComponent = <Battery4BarIcon sx={{ color: "orange" }} />;
  else if (battery >= 50 && battery < 70)
    batteryComponent = <Battery5BarIcon sx={{ color: "green" }} />;
  else if (battery >= 70 && battery < 90)
    batteryComponent = <Battery6BarIcon sx={{ color: "green" }} />;
  else if (battery >= 90 && battery <= 100)
    batteryComponent = <BatteryFullIcon sx={{ color: "green" }} />;

  return batteryComponent;
};

/**
 * 「Location」フィルター機能定義
 */
const locationFilterOperators: GridFilterOperator[] = [
  {
    label: "contains",
    value: "contains",
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (!filterItem.field || !filterItem.value || !filterItem.operator) {
        return null;
      }

      return (params): boolean => {
        const paramsValue = params["row"]["location"];
        const filterValue = filterItem["value"];
        return paramsValue.includes(filterValue);
      };
    },
    InputComponent: GridFilterInputValue,
    InputComponentProps: { type: "string" },
    getValueAsString: (value: string) => `${value}`,
  },
];

/**
 * 「Location」ソート処理
 * @param v1
 * @param v2
 * @param param1
 * @param param2
 * @returns
 */
const locationSortComparator = (v1, v2, param1: any, param2: any) => {
  const value1 = param1["api"].getRow(param1["id"])["location"];
  const value1SortDirection = param2["api"].getSortModel(param1["id"])[0][
    "sort"
  ];
  const value2 = param2["api"].getRow(param2["id"])["location"];
  const value2SortDirection = param1["api"].getSortModel(param1["id"])[0][
    "sort"
  ];

  if (isTextEmpty(value1)) {
    if (value1SortDirection === "asc") return 1;
    else return -1;
  } else if (isTextEmpty(value2)) {
    if (value2SortDirection === "asc") return -1;
    else return 1;
  } else {
    return value1.localeCompare(value2);
  }
};

/**
 * 「Location」描画処理
 * @param params
 * @returns
 */
const locationRenderCell = (params: GridRenderCellParams) => {
  const row = params["row"];
  const location = row["classroomNumber"];
  return <Typography>{location}</Typography>;
};

/**
 * 「First Receive Time」ソート処理
 * @param v1
 * @param v2
 * @param param1
 * @param param2
 * @returns
 */
const firstReceiveTimeSortComparator = (v1, v2, param1: any, param2: any) => {
  const value1 = param1["api"].getRow(param1["id"])["firstReceiveTime"];
  const value2 = param2["api"].getRow(param2["id"])["firstReceiveTime"];

  if (isEmpty(value1)) {
    return 1;
  } else if (isEmpty(value2)) {
    return -1;
  } else {
    return value1.localeCompare(value2);
  }
};

/**
 * 「First Receive Time」描画処理
 * @param params
 * @returns
 */
const firstReceiveTimeRenderCell = (params: GridRenderCellParams) => {
  const row = params["row"];
  const firstReceiveTime = row["firstReceiveTime"];
  const parseValue = moment(firstReceiveTime);

  return (
    <Typography>
      {parseValue.isValid() ? parseValue.format(DATETIME_FORMAT) : ""}
    </Typography>
  );
};

/**
 * 「Last Receive Time」ソート処理
 * @param v1
 * @param v2
 * @param param1
 * @param param2
 * @returns
 */
const lastReceiveTimeSortComparator = (v1, v2, param1: any, param2: any) => {
  const value1 = param1["api"].getRow(param1["id"])["lastReceiveTime"];
  const value2 = param2["api"].getRow(param2["id"])["lastReceiveTime"];

  if (isEmpty(value1)) {
    return 1;
  } else if (isEmpty(value2)) {
    return -1;
  } else {
    return value1.localeCompare(value2);
  }
};

/**
 * 「Last Receive Time」描画処理
 * @param params
 * @returns
 */
const lastReceiveTimeRenderCell = (params: GridRenderCellParams) => {
  const row = params["row"];
  const lastReceiveTime = row["lastReceiveTime"];
  const parseValue = moment(lastReceiveTime);

  return (
    <Typography>
      {parseValue.isValid() ? parseValue.format(DATETIME_FORMAT) : ""}
    </Typography>
  );
};

export {
  staySortComparator,
  stayRenderCell,
  distanceSortComparator,
  distanceRenderCell,
  batteryFilterOperators,
  batterySortComparator,
  batteryRenderCell,
  locationFilterOperators,
  locationSortComparator,
  locationRenderCell,
  firstReceiveTimeSortComparator,
  firstReceiveTimeRenderCell,
  lastReceiveTimeSortComparator,
  lastReceiveTimeRenderCell,
};
